"use client";

// A tiny bridge from panels rendered inside OsShell back to the shell itself —
// so the Activity panel can drive the single living organism (age it as you
// scroll the timeline) and jump within the shell (SPA navigation to a project)
// without prop-drilling through the panel boundary. Falls back to no-ops so the
// consumers also work standalone (e.g. the landing sample feed).

import { createContext, useContext } from "react";
import type { RootAgent } from "../../lib/os/tree";

export type OsShellApi = {
  /** Navigate within the shell (project paths stay SPA). `view` lands on that
   *  view of the target (e.g. a code project's "repo") when the level offers it;
   *  otherwise the level's default view, as always. Resolves once the shell has
   *  moved (a project not yet in the tree is loaded first). */
  goTo: (target: string, opts?: { view?: string }) => void | Promise<void>;
  /** A panel reporting which of its own views is showing (the activity panel's
   *  "repos" seats netti bottom-left). null when it leaves. Optional: only the
   *  shell provides it. */
  setPanelView?: (view: string | null) => void;
  /** WHERE THE SHELL IS, as a path ("/agents", "/agents/grok-bot",
   *  "/agents/grok-bot/deep-search"). A panel is a pre-built node the shell cannot
   *  see into, and the agents panel stands at several paths (its home and every
   *  agent's own page), so it reads this to know which of them to draw. The
   *  stage is keyed by path, so the panel remounts on every move and the value it
   *  reads at mount is the one it renders. Undefined outside the shell: the agents
   *  panel then treats it as "/agents". */
  path?: string;
  /** Hand the shell every agent the person keeps, so its tree knows them: each one
   *  becomes a real path under /agents (the crumbs, the navigator's title, the ≡
   *  menu, a reload and a link all resolve to it) and the agents chip shows the
   *  count. Call it after every SUCCESSFUL read of the list, and after a make or
   *  a remove; never with an empty list for a read that failed ("0 agents" is a
   *  claim, a failed read is not one).
   *
   *  ORDER MATTERS when you also navigate: call setAgentRows with the new row
   *  BEFORE goTo(`/agents/<slug>`). goTo resolves a path under /agents against the
   *  rows handed in here even in the same breath, so a just-made agent opens on
   *  its page. A path whose agent is gone from the rows lands on the nearest
   *  place that still exists (/agents), after a short grace. */
  setAgentRows?: (rows: RootAgent[]) => void;
  /** Turn netti with the orbit. The agents orbit reports its revolve angle (radians,
   *  0 at rest) as the person wheels or drags it; the organism follows at the same
   *  factor OrbitView uses, so the two turn like gears with netti leading (NETTI_FACTOR
   *  0.6 on the shell side). The shell zeroes it on every path change and when the
   *  panel is not showing its orbit, so a page that leaves does not leave netti turned. */
  setOrbitSpin?: (rad: number) => void;
  /** Override the organism's age (0..1), or null to hand it back to growth.
   *  Imperative — dispatched straight to the organism (no React re-render), so
   *  scrolling the timeline scrubs the bloom at 60fps without churning the shell. */
  setOrganismAge: (age: number | null) => void;
  /** The organism's NATURAL size right now — its true maturity earned only from
   *  how much memory exists (ageFromMemory). The Law: scrolling/aging may flex the
   *  bloom a little for effect but must never inflate it past this, because size
   *  is a truthful readout of memory. Timelines anchor "today" to this and only
   *  ever de-age BELOW it toward the sprout. */
  naturalAge: number;
  /** whether the repo mark may show beside a code project's name — founders
   *  only while the repo surface is gated (canSee(F.projectRepo)). Optional:
   *  outside the shell nothing shows it. */
  showCodeMarks?: boolean;
  /** The navigator's rail slot: an element inside the pinned head, under the
   *  view chips, that rides the fold with them. A view may portal ONE quiet line
   *  into it (the repo view's current day) so a label that belongs to the head
   *  sits in the head instead of floating under it. null outside the shell or
   *  before the head mounts; the slot is empty (0 tall) when no view uses it. */
  railSlot?: HTMLElement | null;
  /** The navigator's chip slot (law_stage, 2026-10-12): where a panel's own view
   *  chips stand on the stage, in the navigator, exactly where a folder's chips
   *  stand, so they ride up with the fold and stay one press away while reading.
   *  A panel hands its row in through components/os/NavChips. null off the stage,
   *  outside the shell, or on a level whose navigator draws chips of its own. */
  chipSlot?: HTMLElement | null;
  /** The crumb's search spot (2026-10-14): a panel that can be searched hands
   *  its field in through components/os/CrumbFind, and the crumb's own search
   *  steps aside while it stands there. null outside the shell. */
  findSlot?: HTMLElement | null;
  /** The viewer's own netti, exactly as the shell draws it (law_one_netti):
   *  the seed, the curated colour, a hand-tuned body when one is saved, the age
   *  it wears now, and the ground it stands on. For surfaces that render a
   *  still of it (the share card). Optional: absent outside the shell. */
  netti?: ShellNetti | null;
  /** The viewer's name and face, for the one place that wears them inside the
   *  content: their memories folder (components/os/Thumb, kind 'memories').
   *  Optional: absent outside the shell (the thumb then draws the cell bare). */
  me?: { name: string; avatarUrl: string | null } | null;
};

/** The viewer's netti as the shell holds it (see OsShell `shellApi`). */
export type ShellNetti = {
  seed: number;
  /** curated ColorState (lib/organism/future) — the engine vivids it at render */
  color: import("../../lib/organism/color").ColorState;
  /** a hand-tuned body patch (the netti tuner), or null */
  body: import("../../lib/netti/tuner").TunerPatch | null;
  /** the age the shell renders now (0..1) */
  age: number;
  /** the ground under it, RGB 0..1 */
  ground: [number, number, number];
};

const OsShellContext = createContext<OsShellApi | null>(null);
export const OsShellProvider = OsShellContext.Provider;

export function useOsShell(): OsShellApi {
  return useContext(OsShellContext) ?? { goTo: () => {}, setPanelView: () => {}, setOrganismAge: () => {}, naturalAge: 0.52 };
}
